import { useState, useEffect } from 'react'
import { Box, CircularProgress, IconButton } from '@mui/material'
import Add from '@mui/icons-material/Add'
import Close from '@mui/icons-material/Close'
import { useFilePondUpload } from 'src/consumer/components/Helpers/PictureUploadComponent/useFilePondUpload'
import { mediaURL } from 'src/consumer/services'
import { useImageUpload } from '../hooks/useImageUpload'
import PrescriptionPreviewModal from './PrescriptionPreviewModal'

/**
 * Self-contained component for image upload with prescription preview and height adjustment
 * Handles: Upload → Crop → Preview Modal → Height Adjustment → Server Upload → Callback
 */
export default function ImageUploadWithHeightAdjustment({
  imageId,
  currentHeight,
  prescriptionTemplate,
  type, // 'header' or 'footer'
  defaultHeight = 2,
  minHeight = 0.5,
  maxHeight = 5,
  onComplete, // (imageId, adjustedHeight) => void
  disabled,
  placeholder = 'Add Image',
  aspectWidth,
  aspectHeight,
  showSlider,
}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [deferredFile, setDeferredFile] = useState(null)
  const [localBlobUrl, setLocalBlobUrl] = useState(null)

  const { uploadImage, isUploading } = useImageUpload()

  // Use deferred upload mode - don't upload until user confirms
  const {
    triggerUpload,
    isUploading: isProcessing,
    editedFile,
    localPreviewUrl,
    clearDeferredFile,
    FilePondComponent,
  } = useFilePondUpload({
    aspectWidth,
    aspectHeight,
    deferUpload: true, // Key: defer upload until confirmation
    onEditComplete: ({ file, blobUrl }) => {
      // Doka editing complete - store file and open modal
      setDeferredFile(file)
      setLocalBlobUrl(blobUrl)
      setModalOpen(true)
    },
  })

  // Cleanup blob URL on unmount or when cleared
  useEffect(() => {
    return () => {
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl)
      }
    }
  }, [localBlobUrl])

  const handleRemove = (e) => {
    e.stopPropagation()
    onComplete(null, currentHeight)
  }

  const handleClick = () => {
    if (!disabled) {
      triggerUpload()
    }
  }

  const handleModalClose = () => {
    if (!isUploading) {
      setModalOpen(false)
      // Clean up deferred file and blob URL
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl)
      }
      setDeferredFile(null)
      setLocalBlobUrl(null)
      clearDeferredFile()
    }
  }

  const handleModalConfirm = async (adjustedHeight) => {
    if (!deferredFile) return

    // Upload file to server
    const uploadedImageId = await uploadImage(deferredFile)

    if (uploadedImageId) {
      // Success - callback with imageId and height
      onComplete(uploadedImageId, adjustedHeight)
      handleModalClose()
    }
    // If upload failed, keep modal open so user can try again or cancel
  }

  return (
    <>
      <FilePondComponent />

      {/* Upload/Preview Box */}
      <Box
        onClick={handleClick}
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 200,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          overflow: 'hidden',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          '&:hover': !disabled && {
            borderColor: '#1976d2',
            backgroundColor: '#fafafa',
          },
        }}
      >
        {isProcessing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!imageId && !isProcessing && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              color: '#1976d2',
            }}
          >
            <Add sx={{ fontSize: 48 }} />
            <Box sx={{ fontSize: 16, fontWeight: 500 }}>{placeholder}</Box>
          </Box>
        )}

        {imageId && !isProcessing && (
          <>
            {!disabled && (
              <IconButton
                onClick={handleRemove}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  zIndex: 5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  },
                }}
                size="small"
              >
                <Close />
              </IconButton>
            )}
            <Box
              component="img"
              src={`${mediaURL}${imageId}`}
              alt="Preview"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        )}
      </Box>

      {/* Preview Modal */}
      <PrescriptionPreviewModal
        open={modalOpen}
        onClose={handleModalClose}
        prescriptionTemplate={prescriptionTemplate}
        blobUrl={localBlobUrl}
        type={type}
        defaultHeight={currentHeight || defaultHeight}
        minHeight={minHeight}
        maxHeight={maxHeight}
        onConfirm={handleModalConfirm}
        isUploading={isUploading}
        showSlider={showSlider}
      />
    </>
  )
}
